'use client'

import { Progress as ProgressPrimitive } from '@base-ui/react/progress'
import { cn } from '@nt/ui/cn'

/**
 * A thin bar on the rule tone. `tone="service"` fills it with the service
 * accent in force (`data-service` on an ancestor); the default is forest.
 */
function Progress({
  className,
  children,
  value,
  tone = 'default',
  ...props
}: ProgressPrimitive.Root.Props & { tone?: 'default' | 'service' }) {
  return (
    <ProgressPrimitive.Root
      value={value}
      data-slot="progress"
      data-tone={tone}
      className={cn('group/progress flex flex-wrap gap-3', className)}
      {...props}
    >
      {children}
      <ProgressTrack>
        <ProgressIndicator />
      </ProgressTrack>
    </ProgressPrimitive.Root>
  )
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
  return (
    <ProgressPrimitive.Track
      className={cn('bg-border h-1.5 rounded-full relative flex w-full items-center overflow-x-hidden', className)}
      data-slot="progress-track"
      {...props}
    />
  )
}

function ProgressIndicator({ className, ...props }: ProgressPrimitive.Indicator.Props) {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn(
        'bg-primary group-data-[tone=service]/progress:bg-service h-full rounded-full transition-all',
        className,
      )}
      {...props}
    />
  )
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
  return (
    <ProgressPrimitive.Label
      className={cn('text-sm font-medium text-foreground', className)}
      data-slot="progress-label"
      {...props}
    />
  )
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
  return (
    <ProgressPrimitive.Value
      className={cn('text-muted-foreground ml-auto text-sm tabular-nums', className)}
      data-slot="progress-value"
      {...props}
    />
  )
}

export { Progress, ProgressIndicator, ProgressLabel, ProgressTrack, ProgressValue }
